import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, Area, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceArea, Cell,
} from "recharts";
import { getPriceHistory, backfillPriceHistory } from "./StockPriceService";
import { sma, ema, rsi, macd, bollinger } from "./StockIndicators";

const RANGES = [
  { key: "3M",  days: 90   },
  { key: "6M",  days: 182  },
  { key: "1Y",  days: 365  },
  { key: "2Y",  days: 730  },
  { key: "5Y",  days: 1825 },
  { key: "ALL", days: null },
];

const OVERLAYS = [
  { key: "EMA9",   label: "EMA 9",   color: "#fb923c" },
  { key: "EMA21",  label: "EMA 21",  color: "#f59e0b" },
  { key: "SMA20",  label: "SMA 20",  color: "#a78bfa" },
  { key: "EMA50",  label: "EMA 50",  color: "#3b82f6" },
  { key: "SMA200", label: "SMA 200", color: "#f472b6" },
  { key: "BB",     label: "Bollinger", color: "#64748b" },
];

const SUB_CHARTS = [
  { key: "RSI14", label: "RSI 14", color: "#818cf8" },
  { key: "MACD",  label: "MACD",   color: "#22c55e"  },
];

function downsample(data, maxPoints = 400) {
  if (data.length <= maxPoints) return data;
  const step = Math.ceil(data.length / maxPoints);
  return data.filter((_, i) => i % step === 0 || i === data.length - 1);
}

function formatDateTick(dateStr, range) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (range === "3M" || range === "6M")
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  if (range === "1Y" || range === "2Y")
    return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  return String(d.getFullYear());
}

function computeAllIndicators(prices, points) {
  const sma20  = sma(prices, 20);
  const sma200 = sma(prices, 200);
  const ema9   = ema(prices, 9);
  const ema21  = ema(prices, 21);
  const ema50  = ema(prices, 50);
  const rsi14  = rsi(prices, 14);
  const macdR  = macd(prices);
  const bbR    = bollinger(prices, 20, 2);

  return points.map((p, i) => ({
    date:       p.priceDate,
    price:      Number(p.price),
    sma20:      sma20[i],
    sma200:     sma200[i],
    ema9:       ema9[i],
    ema21:      ema21[i],
    ema50:      ema50[i],
    rsi14:      rsi14[i],
    macdLine:   macdR.macd[i],
    macdSignal: macdR.signal[i],
    macdHist:   macdR.histogram[i],
    bbUpper:    bbR.upper[i],
    bbLower:    bbR.lower[i],
  }));
}

function StatPill({ label, value, color }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ color: "var(--color-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </span>
      <span style={{ fontWeight: 600, color: color || "var(--color-text)", fontSize: 13 }}>
        {value}
      </span>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "var(--color-surface)", border: "1px solid var(--color-border)",
      borderRadius: 8, padding: "8px 12px", fontSize: 12, minWidth: 130,
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--color-muted)" }}>{label}</div>
      {payload.map((p) =>
        p.value != null && (
          <div key={p.name} style={{ color: p.color || "var(--color-text)", marginBottom: 1 }}>
            {p.name}: {typeof p.value === "number" ? `₹${p.value.toFixed(2)}` : p.value}
          </div>
        )
      )}
    </div>
  );
}

export default function StockChart({ symbol, stockName, onClose }) {
  const [history, setHistory]           = useState({ points: [], stockName: null });
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [range, setRange]               = useState("1Y");
  const [activeOverlays, setActiveOverlays]     = useState(() => new Set(["EMA21", "EMA50"]));
  const [activeSubCharts, setActiveSubCharts]   = useState(() => new Set(["RSI14"]));
  const [backfilling, setBackfilling]   = useState(false);
  const [backfillMsg, setBackfillMsg]   = useState(null);

  const fetchHistory = useCallback(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    getPriceHistory(symbol)
      .then((data) => setHistory(data || { points: [] }))
      .catch((e) => setError(e.message || "Failed to load price history"))
      .finally(() => setLoading(false));
  }, [symbol]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillMsg(null);
    try {
      const result = await backfillPriceHistory(symbol);
      setBackfillMsg(`Loaded ${result.saved ?? "?"} data points from Yahoo Finance`);
      fetchHistory();
    } catch (e) {
      setBackfillMsg(`Failed: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  const toggleOverlay = (key) => setActiveOverlays((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const toggleSubChart = (key) => setActiveSubCharts((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const allPoints = useMemo(() => history?.points || [], [history]);

  // Compute indicators on FULL dataset to avoid warmup distortion
  const allData = useMemo(() => {
    if (allPoints.length === 0) return [];
    return computeAllIndicators(allPoints.map((p) => Number(p.price)), allPoints);
  }, [allPoints]);

  // Filter to selected range AFTER full-dataset computation
  const filteredData = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    if (!r || r.days === null) return allData;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - r.days);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return allData.filter((d) => d.date >= cutoffStr);
  }, [allData, range]);

  // Downsample for 5Y/ALL to keep rendering fast
  const chartData = useMemo(
    () => (range === "5Y" || range === "ALL") ? downsample(filteredData, 400) : filteredData,
    [filteredData, range]
  );

  const stats = useMemo(() => {
    if (filteredData.length === 0) return null;
    const prices = filteredData.map((d) => d.price);
    const first  = prices[0];
    const last   = prices[prices.length - 1];
    const pct    = ((last - first) / first) * 100;
    const high   = Math.max(...prices);
    const low    = Math.min(...prices);

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const yrStr    = oneYearAgo.toISOString().slice(0, 10);
    const yrPrices = allData.filter((d) => d.date >= yrStr).map((d) => d.price);
    const high52   = yrPrices.length > 0 ? Math.max(...yrPrices) : null;
    const low52    = yrPrices.length > 0 ? Math.min(...yrPrices) : null;

    return { pct, high, low, high52, low52 };
  }, [filteredData, allData]);

  const priceUp    = stats ? stats.pct >= 0 : true;
  const priceColor = priceUp ? "#22c55e" : "#ef4444";

  const latest       = chartData[chartData.length - 1];
  const prevPoint    = chartData[chartData.length - 2];
  const dayChangePct = latest && prevPoint
    ? ((latest.price - prevPoint.price) / prevPoint.price) * 100
    : null;

  const tickInterval = useMemo(() => {
    const n = chartData.length;
    if (n <= 60)  return 0;
    if (n <= 120) return Math.floor(n / 12);
    if (n <= 400) return Math.floor(n / 10);
    return Math.floor(n / 8);
  }, [chartData.length]);

  const dateFormatter = useCallback((v) => formatDateTick(v, range), [range]);

  const priceTickFormatter = (v) =>
    v >= 1000 ? `₹${(v / 1000).toFixed(1)}k` : `₹${v.toFixed(0)}`;

  return (
    <div className="card">
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {symbol}{(stockName || history.stockName) ? ` · ${stockName || history.stockName}` : ""}
          </div>
          {latest && (
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 4 }}>
              <span style={{ fontSize: 24, fontWeight: 800 }}>₹{Number(latest.price).toFixed(2)}</span>
              {dayChangePct !== null && (
                <span style={{ color: dayChangePct >= 0 ? "#22c55e" : "#ef4444", fontWeight: 600, fontSize: 14 }}>
                  {dayChangePct >= 0 ? "▲" : "▼"} {Math.abs(dayChangePct).toFixed(2)}%
                </span>
              )}
              <span style={{ fontSize: 11, color: "var(--color-muted)" }}>as of {latest.date}</span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleBackfill}
            disabled={backfilling}
            title="Pull 10 years of daily prices from Yahoo Finance"
          >
            {backfilling ? "Loading…" : "📥 10Y History"}
          </button>
          {onClose && (
            <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ fontWeight: 700 }}>✕</button>
          )}
        </div>
      </div>

      {backfillMsg && (
        <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 6 }}>{backfillMsg}</div>
      )}

      {loading && <div style={{ color: "var(--color-muted)", padding: 16 }}>Loading price history…</div>}
      {error   && <div className="stocks-bad">{error}</div>}

      {!loading && !error && (
        <>
          {/* ── Stats bar ── */}
          {stats && (
            <div style={{
              display: "flex", gap: 20, flexWrap: "wrap",
              padding: "6px 0 10px", borderBottom: "1px solid var(--color-border)", marginBottom: 10,
            }}>
              <StatPill
                label={`${range} change`}
                value={`${stats.pct >= 0 ? "+" : ""}${stats.pct.toFixed(2)}%`}
                color={stats.pct >= 0 ? "#22c55e" : "#ef4444"}
              />
              <StatPill label={`${range} high`} value={`₹${stats.high.toFixed(2)}`} />
              <StatPill label={`${range} low`}  value={`₹${stats.low.toFixed(2)}`}  />
              {stats.high52 != null && <StatPill label="52W high" value={`₹${stats.high52.toFixed(2)}`} />}
              {stats.low52  != null && <StatPill label="52W low"  value={`₹${stats.low52.toFixed(2)}`}  />}
              <StatPill label="Days loaded" value={allPoints.length.toLocaleString()} />
            </div>
          )}

          {/* ── Range buttons ── */}
          <div className="stocks-tabs" style={{ marginBottom: 10 }}>
            {RANGES.map((r) => (
              <button
                key={r.key}
                className={`tab-btn btn-sm ${range === r.key ? "active" : ""}`}
                onClick={() => setRange(r.key)}
              >
                {r.key}
              </button>
            ))}
          </div>

          {/* ── Indicator toggles ── */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {OVERLAYS.map((o) => {
              const on = activeOverlays.has(o.key);
              return (
                <button
                  key={o.key}
                  onClick={() => toggleOverlay(o.key)}
                  style={{
                    fontSize: 11, padding: "2px 10px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${on ? o.color : "var(--color-border)"}`,
                    background: on ? `${o.color}22` : "transparent",
                    color: on ? o.color : "var(--color-muted)",
                    transition: "all 0.15s",
                  }}
                >
                  {o.label}
                </button>
              );
            })}
            {SUB_CHARTS.map((s) => {
              const on = activeSubCharts.has(s.key);
              return (
                <button
                  key={s.key}
                  onClick={() => toggleSubChart(s.key)}
                  style={{
                    fontSize: 11, padding: "2px 10px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${on ? s.color : "var(--color-border)"}`,
                    background: on ? `${s.color}22` : "transparent",
                    color: on ? s.color : "var(--color-muted)",
                    transition: "all 0.15s",
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* ── Main price chart ── */}
          {chartData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="priceAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={priceColor} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={priceColor} stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.4} />
                <XAxis
                  dataKey="date"
                  tickFormatter={dateFormatter}
                  tick={{ fontSize: 10, fill: "var(--color-muted)" }}
                  interval={tickInterval}
                  minTickGap={44}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  tick={{ fontSize: 10, fill: "var(--color-muted)" }}
                  tickFormatter={priceTickFormatter}
                  width={58}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone" dataKey="price" name="Price"
                  stroke={priceColor} strokeWidth={2}
                  fill="url(#priceAreaGrad)" dot={false} activeDot={{ r: 4 }}
                />
                {activeOverlays.has("EMA9") && (
                  <Line type="monotone" dataKey="ema9" name="EMA 9"
                    stroke="#fb923c" dot={false} strokeWidth={1.5} connectNulls />
                )}
                {activeOverlays.has("EMA21") && (
                  <Line type="monotone" dataKey="ema21" name="EMA 21"
                    stroke="#f59e0b" dot={false} strokeWidth={1.5} connectNulls />
                )}
                {activeOverlays.has("SMA20") && (
                  <Line type="monotone" dataKey="sma20" name="SMA 20"
                    stroke="#a78bfa" dot={false} strokeWidth={1.5} connectNulls />
                )}
                {activeOverlays.has("EMA50") && (
                  <Line type="monotone" dataKey="ema50" name="EMA 50"
                    stroke="#3b82f6" dot={false} strokeWidth={1.5} connectNulls />
                )}
                {activeOverlays.has("SMA200") && (
                  <Line type="monotone" dataKey="sma200" name="SMA 200"
                    stroke="#f472b6" dot={false} strokeWidth={2} connectNulls />
                )}
                {activeOverlays.has("BB") && (
                  <>
                    <Line type="monotone" dataKey="bbUpper" name="BB Upper"
                      stroke="#64748b" dot={false} strokeWidth={1} strokeDasharray="4 2" connectNulls />
                    <Line type="monotone" dataKey="bbLower" name="BB Lower"
                      stroke="#64748b" dot={false} strokeWidth={1} strokeDasharray="4 2" connectNulls />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: "var(--color-muted)", padding: 24, textAlign: "center" }}>
              {allPoints.length === 0
                ? `No price history for ${symbol}. Click "📥 10Y History" to load data from Yahoo Finance.`
                : `Not enough data in the ${range} window — try a wider range or load more history.`}
            </div>
          )}

          {/* ── RSI sub-chart ── */}
          {activeSubCharts.has("RSI14") && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-muted)", marginBottom: 4 }}>RSI (14)</div>
              {chartData.length > 14 ? (
                <ResponsiveContainer width="100%" height={110}>
                  <ComposedChart data={chartData} margin={{ top: 2, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.3} />
                    <XAxis dataKey="date" tickFormatter={dateFormatter}
                      tick={{ fontSize: 9, fill: "var(--color-muted)" }}
                      interval={tickInterval} minTickGap={44} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "var(--color-muted)" }} width={28} />
                    <Tooltip content={<ChartTooltip />} />
                    <ReferenceArea y1={70} y2={100} fill="rgba(239,68,68,0.08)" />
                    <ReferenceArea y1={0}   y2={30}  fill="rgba(34,197,94,0.08)" />
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.6} />
                    <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="3 3" strokeOpacity={0.6} />
                    <ReferenceLine y={50} stroke="var(--color-border)" strokeDasharray="2 4" strokeOpacity={0.5} />
                    <Line type="monotone" dataKey="rsi14" name="RSI"
                      stroke="#818cf8" dot={false} strokeWidth={1.5} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: "var(--color-muted)", fontSize: 12 }}>
                  Need more data points in this range to compute RSI.
                </div>
              )}
            </div>
          )}

          {/* ── MACD sub-chart ── */}
          {activeSubCharts.has("MACD") && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-muted)", marginBottom: 4 }}>MACD (12, 26, 9)</div>
              {chartData.length > 35 ? (
                <ResponsiveContainer width="100%" height={120}>
                  <ComposedChart data={chartData} margin={{ top: 2, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.3} />
                    <XAxis dataKey="date" tickFormatter={dateFormatter}
                      tick={{ fontSize: 9, fill: "var(--color-muted)" }}
                      interval={tickInterval} minTickGap={44} />
                    <YAxis tick={{ fontSize: 9, fill: "var(--color-muted)" }} width={36} />
                    <Tooltip content={<ChartTooltip />} />
                    <ReferenceLine y={0} stroke="var(--color-border)" strokeOpacity={0.7} />
                    <Bar dataKey="macdHist" name="Histogram" barSize={2} isAnimationActive={false}>
                      {chartData.map((entry, i) => (
                        <Cell key={i}
                          fill={(entry.macdHist ?? 0) >= 0 ? "rgba(34,197,94,0.75)" : "rgba(239,68,68,0.75)"}
                        />
                      ))}
                    </Bar>
                    <Line type="monotone" dataKey="macdLine" name="MACD"
                      stroke="#22c55e" dot={false} strokeWidth={1.5} connectNulls />
                    <Line type="monotone" dataKey="macdSignal" name="Signal"
                      stroke="#f87171" dot={false} strokeWidth={1.5} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: "var(--color-muted)", fontSize: 12 }}>
                  Need more data points in this range to compute MACD.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
